import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ReviewCard from "../Utils/Cards/review-card-component";
import ElegantReviewCard from '../Utils/Cards/review-card-elegant-component';
import { Helmet } from 'react-helmet';
import './review-list-styles.css';

const ReviewList = ({ reviews, useElegant = false, seoTag = true }) => {
  const scrollContainerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(1); // 1 for right, -1 for left
  const businessInfo = useSelector(state => (state.systemData?.businessInfo));

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Smooth continuous scroll function
    const smoothScroll = () => {
      if (!isHovering) {
        container.scrollLeft += scrollDirection; // Increment or decrement based on direction

        // Reverse direction at ends
        if (container.scrollLeft >= container.scrollWidth - container.clientWidth ||
          container.scrollLeft <= 0) {
          setScrollDirection(-scrollDirection);
        }
      }
    };

    // Set up interval for continuous scrolling
    const interval = setInterval(smoothScroll, 20); // Adjust time for speed

    // Event listeners for hover state
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    // Clean-up function
    return () => {
      clearInterval(interval);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isHovering, scrollDirection]);

  return (
    <>
      {seoTag && reviews?.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'LocalBusiness',
              name: businessInfo.businessName,
              image: businessInfo.mainLogo || '',
              url: businessInfo.website || '',
              telephone: businessInfo.phone || '',
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '5',
                bestRating: '5',
                reviewCount: reviews.length
              },
              review: reviews.map(review => ({
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: review.name
                },
                reviewBody: review.reviewText,
                datePublished: review.reviewDate,
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: '5',
                  bestRating: '5'
                }
              }))
            })}
          </script>
        </Helmet>
      )}
      <div ref={scrollContainerRef} dir={'ltr'} className='flex overflow-auto w-screen '>
        {reviews?.map((review, index) => (
          <div key={index} className="review-card-wrapper">
            {useElegant ? (
              <ElegantReviewCard
                name={review.name}
                reviewText={review.reviewText}
                shortText={review.shortText}
                profile={review.profile}
                image={review.image}
              />
            ) : (
              <ReviewCard
                name={review.name}
                joinDate={review.joinDate}
                reviewDate={review.reviewDate}
                reviewText={review.reviewText}
                helpfulCount={review.helpfulCount}
                shortText={review.shortText}
                image={review.image}
                profile={review.profile}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default ReviewList;
